import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  Spinner,
  Tooltip,
  EmptyState,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/UI";
import { smsTemplatesAPI } from "../../services/api";
import toast from "react-hot-toast";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineChatAlt,
  HiOutlineEye,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineSearch,
} from "react-icons/hi";

const categoryOptions = [
  { value: "", label: "All Categories" },
  { value: "greeting", label: "Greeting" },
  { value: "follow_up", label: "Follow Up" },
  { value: "reminder", label: "Reminder" },
  { value: "offer", label: "Offer" },
  { value: "confirmation", label: "Confirmation" },
  { value: "custom", label: "Custom" },
];

const SMSTemplates = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    message: "",
    variables: [],
    category: "custom",
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [newVariable, setNewVariable] = useState("");

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["sms-templates", categoryFilter, statusFilter],
    queryFn: async () => {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.isActive = statusFilter;
      const response = await smsTemplatesAPI.getAll(params);
      return response.data?.data || [];
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => smsTemplatesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["sms-templates"]);
      toast.success("Template created successfully");
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create template");
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => smsTemplatesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["sms-templates"]);
      toast.success("Template updated successfully");
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update template");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => smsTemplatesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["sms-templates"]);
      toast.success("Template deleted successfully");
      setShowDeleteModal(false);
      setSelectedTemplate(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete template");
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: (id) => smsTemplatesAPI.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["sms-templates"]);
      toast.success("Template status updated");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  const templates = templatesData || [];

  // Filter by search
  const filteredTemplates = search
    ? templates.filter(
        (t) =>
          t.name?.toLowerCase().includes(search.toLowerCase()) ||
          t.message?.toLowerCase().includes(search.toLowerCase()),
      )
    : templates;

  const handleOpenModal = (template = null) => {
    setFormErrors({});
    if (template) {
      setSelectedTemplate(template);
      setFormData({
        name: template.name,
        message: template.message,
        variables: template.variables || [],
        category: template.category || "custom",
        isActive: template.isActive,
      });
    } else {
      setSelectedTemplate(null);
      setFormData({
        name: "",
        message: "",
        variables: [],
        category: "custom",
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setFormData({
      name: "",
      message: "",
      variables: [],
      category: "custom",
      isActive: true,
    });
    setFormErrors({});
    setNewVariable("");
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowViewModal(true);
  };

  const handleDeleteClick = (template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const handleAddVariable = () => {
    if (
      newVariable.trim() &&
      !formData.variables.includes(newVariable.trim())
    ) {
      setFormData({
        ...formData,
        variables: [...formData.variables, newVariable.trim()],
      });
      setNewVariable("");
    }
  };

  const handleRemoveVariable = (index) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Template name is required";
    }
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error(Object.values(errors)[0]);
      return;
    }
    setFormErrors({});

    // Extract variables from message
    const variableRegex = /\{\{(\w+)\}\}/g;
    const extractedVars = [];
    let match;
    while ((match = variableRegex.exec(formData.message)) !== null) {
      if (!extractedVars.includes(match[1])) {
        extractedVars.push(match[1]);
      }
    }

    const data = {
      ...formData,
      variables: extractedVars.length > 0 ? extractedVars : formData.variables,
    };

    if (selectedTemplate) {
      updateMutation.mutate({ id: selectedTemplate._id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const getCategoryBadge = (category) => {
    const variants = {
      greeting: "success",
      follow_up: "warning",
      reminder: "info",
      offer: "primary",
      confirmation: "success",
      custom: "default",
    };
    return variants[category] || "default";
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
          <div className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">
            {templates.length}
          </div>
          <div className="text-xs lg:text-sm text-gray-500">Templates</div>
        </Card>
        <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
          <div className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">
            {templates.filter((t) => t.isActive).length}
          </div>
          <div className="text-xs lg:text-sm text-green-600">Active</div>
        </Card>
        <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-red-50">
          <div className="text-base sm:text-lg lg:text-2xl font-bold text-red-600">
            {templates.filter((t) => !t.isActive).length}
          </div>
          <div className="text-xs lg:text-sm text-red-600">Inactive</div>
        </Card>
        <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-blue-50">
          <div className="text-base sm:text-lg lg:text-2xl font-bold text-blue-600">
            {new Set(templates.map((t) => t.category)).size}
          </div>
          <div className="text-xs lg:text-sm text-blue-600">Categories</div>
        </Card>
      </div>

      {/* Filters */}
      <Card padding="p-3 lg:p-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Header with Add Button */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
              SMS Templates
            </h3>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleOpenModal()}
            >
              <div className="flex justify-center items-center">
                <div>
                  <HiOutlinePlus className="w-4 h-4 sm:mr-1" />
                </div>
                <div>
                  <span className="hidden sm:inline">Add Template</span>
                  <span className="sm:hidden">Add</span>
                </div>
              </div>
            </Button>
          </div>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                icon={<HiOutlineSearch className="w-5 h-5 text-gray-400" />}
              />
            </div>
            <div className="flex gap-2">
              <Select
                options={categoryOptions}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="flex-1 min-w-[150px]"
              />
              <Select
                options={[
                  { value: "", label: "All Status" },
                  { value: "true", label: "Active" },
                  { value: "false", label: "Inactive" },
                ]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 min-w-[120px]"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Templates List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <EmptyState
            icon={<HiOutlineChatAlt className="w-8 h-8 text-gray-400" />}
            title="No templates found"
            description="Create your first SMS template to get started"
            action={
              <Button
                variant="primary"
                onClick={() => handleOpenModal()}
              >
                <div className="flex justify-center items-center">
                  <div>
                    <HiOutlinePlus className="w-4 h-4 sm:mr-1" />
                  </div>
                  <div>
                    <span>Add Template</span>
                  </div>
                </div>
              </Button>
            }
          />
        </Card>
      ) : (
        <Card padding="p-0">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow hoverable={false}>
                  <TableCell header className="min-w-[120px]">
                    Name
                  </TableCell>
                  <TableCell header className="min-w-[100px]">
                    Category
                  </TableCell>
                  <TableCell header className="min-w-[200px]">
                    Message Preview
                  </TableCell>
                  <TableCell header className="min-w-[100px]">
                    Variables
                  </TableCell>
                  <TableCell header className="min-w-[80px]">
                    Status
                  </TableCell>
                  <TableCell header className="min-w-[120px]">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.map((template) => (
                  <TableRow key={template._id}>
                    <TableCell>
                      <div className="font-medium text-gray-900 whitespace-nowrap">
                        {template.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getCategoryBadge(template.category)}
                        size="sm"
                      >
                        {template.category?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px] lg:max-w-xs truncate text-gray-600">
                        {template.message}
                      </div>
                    </TableCell>
                    <TableCell>
                      {template.variables?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {template.variables.slice(0, 3).map((v, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded whitespace-nowrap"
                            >
                              {`{{${v}}}`}
                            </span>
                          ))}
                          {template.variables.length > 3 && (
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              +{template.variables.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={template.isActive ? "success" : "danger"}
                        size="sm"
                      >
                        {template.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-nowrap">
                        <Tooltip text="View Template">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlineEye className="w-4 h-4" />}
                            onClick={() => handleViewTemplate(template)}
                          />
                        </Tooltip>
                        <Tooltip text="Edit Template">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlinePencil className="w-4 h-4" />}
                            onClick={() => handleOpenModal(template)}
                          />
                        </Tooltip>
                        <Tooltip text={template.isActive ? "Deactivate" : "Activate"}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={
                              template.isActive ? (
                                <HiOutlineX className="w-4 h-4 text-red-500" />
                              ) : (
                                <HiOutlineCheck className="w-4 h-4 text-green-500" />
                              )
                            }
                            onClick={() => toggleMutation.mutate(template._id)}
                          />
                        </Tooltip>
                        <Tooltip text="Delete Template">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={
                              <HiOutlineTrash className="w-4 h-4 text-red-500" />
                            }
                            onClick={() => handleDeleteClick(template)}
                          />
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Header Row */}
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {template.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant={getCategoryBadge(template.category)}
                        size="sm"
                      >
                        {template.category?.replace("_", " ")}
                      </Badge>
                      <Badge
                        variant={template.isActive ? "success" : "danger"}
                        size="sm"
                      >
                        {template.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Message Preview */}
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {template.message}
                </p>

                {/* Variables */}
                {template.variables?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.variables.slice(0, 4).map((v, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded"
                      >
                        {`{{${v}}}`}
                      </span>
                    ))}
                    {template.variables.length > 4 && (
                      <span className="px-2 py-0.5 text-xs text-gray-400">
                        +{template.variables.length - 4} more
                      </span>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1">
                    <Tooltip text="View Template">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlineEye className="w-4 h-4 text-gray-500" />}
                        onClick={() => handleViewTemplate(template)}
                      />
                    </Tooltip>
                    <Tooltip text="Edit Template">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={
                          <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                        }
                        onClick={() => handleOpenModal(template)}
                      />
                    </Tooltip>
                    <Tooltip text={template.isActive ? "Deactivate" : "Activate"}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={
                          template.isActive ? (
                            <HiOutlineX className="w-4 h-4 text-orange-500" />
                          ) : (
                            <HiOutlineCheck className="w-4 h-4 text-green-500" />
                          )
                        }
                        onClick={() => toggleMutation.mutate(template._id)}
                      />
                    </Tooltip>
                    <Tooltip text="Delete Template">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                        onClick={() => handleDeleteClick(template)}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={selectedTemplate ? "Edit Template" : "Add New Template"}
        size="lg"
        headerClassName="dark:bg-[linear-gradient(120deg,rgba(6,95,70,0.92),rgba(4,47,66,0.90)_50%,rgba(2,6,23,0.95))] dark:border-b dark:border-emerald-500/40 dark:shadow-[0_4px_20px_rgba(16,185,129,0.35)]"
      >
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          <Input
            label={
              <>
                <span className="font-medium">Template Name <span className="text-red-500">*</span></span>
              </>
            }
            inputClassName="px-4 py-3"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
            }}
            placeholder="e.g., Welcome Message"
            error={formErrors.name}
          />

          <Select
            label="Category"
            inputClassName="px-4 py-3"
            options={categoryOptions.filter((o) => o.value !== "")}
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              className={`w-full px-4 py-3 rounded-xl border-2 text-sm sm:text-base focus:ring-0 focus:outline-none transition-colors ${
                formErrors.message
                  ? 'border-danger-500 focus:border-danger-500'
                  : 'border-gray-200 focus:border-primary-500'
              }`}
              rows={4}
              maxLength={480}
              value={formData.message}
              onChange={(e) => {
                setFormData({ ...formData, message: e.target.value });
                if (formErrors.message) setFormErrors({ ...formErrors, message: '' });
              }}
              placeholder="Hello {{name}}, thank you for contacting us!"
            />
            {formErrors.message && (
              <p className="text-xs text-danger-600 font-medium mt-1">{formErrors.message}</p>
            )}
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">
                Use {"{{variable}}"} syntax for dynamic values (e.g., {"{{name}}"}
                , {"{{company}}"})
              </p>
              <p className={`text-xs font-medium ${formData.message.length > 400 ? 'text-danger-600' : 'text-gray-400'}`}>
                {formData.message.length}/480 chars
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Variables (auto-detected from message)
            </label>
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2">
              {formData.variables.map((v, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 bg-primary-50 text-primary-700 rounded-lg text-xs sm:text-sm"
                >
                  {`{{${v}}}`}
                  <button
                    type="button"
                    onClick={() => handleRemoveVariable(i)}
                    className="text-primary-500 hover:text-primary-700"
                  >
                    <HiOutlineX className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                inputClassName="px-4 py-3"
                placeholder="Add variable"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleAddVariable())
                }
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddVariable}
              >
                Add
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="smsIsActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData({ ...formData, isActive: e.target.checked })
              }
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="smsIsActive" className="text-sm text-gray-700">
              Active (template will be available for use)
            </label>
          </div>

          <div className="flex gap-2 sm:gap-3 justify-end pt-3 sm:pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {selectedTemplate ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Template Details"
        size="md"
      >
        {selectedTemplate && (
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="text-xs sm:text-sm text-gray-500">
                Template Name
              </label>
              <p className="font-medium text-gray-900">
                {selectedTemplate.name}
              </p>
            </div>
            <div>
              <label className="text-xs sm:text-sm text-gray-500">
                Category
              </label>
              <p>
                <Badge
                  variant={getCategoryBadge(selectedTemplate.category)}
                  size="sm"
                >
                  {selectedTemplate.category?.replace("_", " ")}
                </Badge>
              </p>
            </div>
            <div>
              <label className="text-xs sm:text-sm text-gray-500">
                Message
              </label>
              <div className="p-2.5 sm:p-3 bg-gray-50 rounded-lg mt-1">
                <p className="text-gray-700 whitespace-pre-wrap text-sm">
                  {selectedTemplate.message}
                </p>
              </div>
            </div>
            {selectedTemplate.variables?.length > 0 && (
              <div>
                <label className="text-xs sm:text-sm text-gray-500">
                  Variables
                </label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1">
                  {selectedTemplate.variables.map((v, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 sm:py-1 bg-blue-50 text-blue-700 rounded text-xs sm:text-sm"
                    >
                      {`{{${v}}}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-xs sm:text-sm text-gray-500">Status</label>
              <p>
                <Badge
                  variant={selectedTemplate.isActive ? "success" : "danger"}
                  size="sm"
                >
                  {selectedTemplate.isActive ? "Active" : "Inactive"}
                </Badge>
              </p>
            </div>
            <div>
              <label className="text-xs sm:text-sm text-gray-500">
                Created
              </label>
              <p className="text-gray-700 text-sm">
                {new Date(selectedTemplate.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Template"
        size="sm"
      >
        <p className="text-gray-600 mb-3 sm:mb-4 text-sm">
          Are you sure you want to delete{" "}
          <span className="font-semibold">{selectedTemplate?.name}</span>? This
          action cannot be undone.
        </p>
        <div className="flex gap-2 sm:gap-3 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => deleteMutation.mutate(selectedTemplate._id)}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default SMSTemplates;